import { useContext, useEffect, useState } from 'react';
import { AdminFormModal, ModalStage } from '../../../AdminFormModal';
import { CheckBox, DatePicker, Input, InputDropdown, Tooltip } from '@platform/components';
import { AdminTableContext } from '../../../AdminTable';
import { AdminTeams, Projects } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import {
  ProjectSummary,
  UpdateTeamAssignmentRequestOrganizationDecision,
} from '@platform/client/schemas';
import { Focusable } from 'react-aria';

export const ProjectAssignmentModal = ({
  teamId,
  cohortId,
  onClose,
  currentProjectId,
  currentOrganizationDecision,
}: {
  teamId: number;
  cohortId: number;
  onClose: () => void;
  currentProjectId?: number;
  currentOrganizationDecision?: UpdateTeamAssignmentRequestOrganizationDecision;
}) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [projectId, setProjectId] = useState(currentProjectId);
  const [draftSearchQuery, setDraftSearchQuery] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [organizationDecision, setOrganizationDecision] =
    useState<UpdateTeamAssignmentRequestOrganizationDecision>(
      currentOrganizationDecision ?? 'pending',
    );
  const [isDeleteRequest, setIsDeleteRequest] = useState<boolean>(false);
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [scheduledSendDate, setScheduledSendDate] = useState<Date>();
  const queryClient = useQueryClient();
  const mutationOptions = {
    onMutate: () => {
      setModalStage('submitting');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AdminTeams.getGetTeamsQueryKey({
          cohort_id: cohortId,
          page: adminQueryOptionsV2.page,
          limit: adminQueryOptionsV2.limit,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: AdminTeams.getGetTeamDetailsQueryKey({ team_id: teamId }),
      });
      setModalStage('success');
    },
    onError: () => {
      setModalStage('error');
    },
  };
  const assignProjectMutator = AdminTeams.useAssignTeamToProject({
    mutation: mutationOptions,
  });
  const deleteAssignmentMutator = AdminTeams.useUnassignTeamFromProject({
    mutation: mutationOptions,
  });
  const updateAssignmentMutator = AdminTeams.useUpdateTeamAssignment({
    mutation: mutationOptions,
  });
  const { data: existingProject } = Projects.useGetProjectSummary(currentProjectId ?? 0, {
    query: {
      enabled: !!currentProjectId,
    },
  });
  return (
    <AdminFormModal
      title={`Manage Project for Team: #${teamId}`}
      modalStage={modalStage}
      onClose={onClose}
      submitCTA={{
        text: `${
          currentProjectId ?
            isDeleteRequest ? 'Delete Assignment'
            : 'Update Assignment'
          : 'Assign Project'
        }`,
        buttonVariant: isDeleteRequest ? 'primary-danger' : 'primary',
        callback: () => {
          if (currentProjectId) {
            if (isDeleteRequest) {
              deleteAssignmentMutator.mutate({
                data: {
                  teamId,
                  projectId: currentProjectId,
                },
              });
            } else {
              updateAssignmentMutator.mutate({
                data: {
                  teamId,
                  projectId: currentProjectId,
                  sendEmail,
                  organizationDecision,
                  scheduledSendDate: scheduledSendDate?.toISOString(),
                },
              });
            }
          } else if (projectId) {
            assignProjectMutator.mutate({
              data: {
                teamId,
                projectId: projectId,
                sendEmail,
                organizationDecision,
                scheduledSendDate: scheduledSendDate?.toISOString(),
              },
            });
          }
        },
        isDisabled: () => !projectId && !isDeleteRequest,
      }}
    >
      <div className='flex flex-col gap-3 w-[400px]'>
        {!currentProjectId && (
          <>
            <Input
              // label='Search for project to assign'
              placeholder='Enter full project id to search'
              ariaLabel='project id search'
              type='inline'
              inputType='number'
              buttonIcon='search-sm'
              buttonText='search'
              buttonOnPress={() => setSearchQuery(draftSearchQuery)}
              value={draftSearchQuery ?? ''}
              onChange={(e) => {
                setDraftSearchQuery(e.target.value);
              }}
              customWidth='w-full'
            />
            <div className='h-20'>
              {searchQuery && (
                <SearchResult
                  searchQuery={searchQuery}
                  onResultIdFound={(id) => setProjectId(id)}
                />
              )}
            </div>
          </>
        )}
        {currentProjectId && existingProject?.projectSummary && (
          <div className='min-h-20'>
            <ProjectSummaryBox projectSummary={existingProject.projectSummary} />
          </div>
        )}
        {!isDeleteRequest && (
          <>
            <RepDecisionDropdown
              onSelectionChange={(key) =>
                setOrganizationDecision(key as UpdateTeamAssignmentRequestOrganizationDecision)
              }
              selectedKey={organizationDecision}
            />
            {organizationDecision === UpdateTeamAssignmentRequestOrganizationDecision.accepted && (
              <DatePicker
                label='Schedule match confirmation email (leave blank to skip sending)'
                onChange={(date) => setScheduledSendDate(date as Date)}
              />
            )}
            {organizationDecision === UpdateTeamAssignmentRequestOrganizationDecision.pending && (
              <CheckBox
                description='Send organization decision email'
                size='md'
                onChange={(isSelected) => setSendEmail(isSelected)}
                aria-label='send email notification'
              />
            )}
          </>
        )}
        {currentProjectId && (
          <>
            {!isDeleteRequest && <OrDivider />}
            <CheckBox
              description='Delete current assignment'
              size='md'
              onChange={(isSelected) => setIsDeleteRequest(isSelected)}
              aria-label='delete current assignment'
            />
          </>
        )}
      </div>
    </AdminFormModal>
  );
};

const OrDivider = () => {
  return (
    <div className='flex items-center gap-4 my-2'>
      <div className='h-px flex-1 bg-gray-300' />
      <span className='text-sm font-medium uppercase tracking-wide text-gray-500'>Or</span>
      <div className='h-px flex-1 bg-gray-300' />
    </div>
  );
};

const RepDecisionDropdown = ({
  selectedKey,
  onSelectionChange,
}: {
  selectedKey: UpdateTeamAssignmentRequestOrganizationDecision;
  onSelectionChange: (key: UpdateTeamAssignmentRequestOrganizationDecision) => void;
}) => {
  return (
    <InputDropdown
      type='default'
      inputDisabled
      label='Organization decision'
      items={[
        {
          text: 'Accepted',
          textValue: UpdateTeamAssignmentRequestOrganizationDecision.accepted,
        },
        {
          text: 'Pending',
          textValue: UpdateTeamAssignmentRequestOrganizationDecision.pending,
        },
        {
          text: 'Declined',
          textValue: UpdateTeamAssignmentRequestOrganizationDecision.declined,
        },
      ]}
      onSelectionChange={(key) =>
        onSelectionChange(key as UpdateTeamAssignmentRequestOrganizationDecision)
      }
      defaultSelectedKey={selectedKey}
    />
  );
};

const SearchResult = ({
  searchQuery,
  onResultIdFound,
}: {
  searchQuery: string;
  onResultIdFound: (id: number) => void;
}) => {
  const { data, isSuccess, isError } = Projects.useGetProjectSummary(parseInt(searchQuery));
  useEffect(() => {
    if (data?.projectSummary?.id) {
      onResultIdFound(data.projectSummary.id);
    }
  }, [data?.projectSummary?.id, onResultIdFound]);
  return (
    <div>
      {isSuccess && data.projectSummary && (
        <ProjectSummaryBox projectSummary={data.projectSummary} />
      )}
      {isError && <div className='text-red-500'>Error searching project</div>}
      {isSuccess && !data.projectSummary && <div>No project found with that ID</div>}
    </div>
  );
};

const ProjectSummaryBox = ({ projectSummary }: { projectSummary: ProjectSummary }) => {
  return (
    <div className='flex flex-col gap-2'>
      <SummaryItem
        label='Project Name'
        value={projectSummary.name}
        href={`/platform/admin/projects/${projectSummary.id}`}
      />
      <SummaryItem
        label='Organization'
        value={projectSummary.organizationName}
        href={`/platform/admin/organizations/${projectSummary.organizationId}`}
      />
    </div>
  );
};

const SummaryItem = ({ label, value, href }: { label: string; value: string; href?: string }) => {
  return (
    <div>
      <p className='text-sm text-gray-500 font-medium'>{label}</p>
      <Tooltip supportingText={value}>
        <Focusable>
          {href ?
            <a
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              className='line-clamp-1 mb-1 blue-link text-xs'
            >
              {value}
            </a>
          : <p className='text-xs line-clamp-1'>{value}</p>}
        </Focusable>
      </Tooltip>
    </div>
  );
};
